import React, { useEffect, useRef } from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, CircularProgress } from "@material-ui/core";
import { primary, darkBlue, lightGrey } from "../../styles/colors";
import { formatCurrency, formatDate, getPaymentStatus } from "./duePaymentsService";

const DuePaymentsTable = ({ payments = [], summary = {}, onRowClick, onLoadMore, loadingMore, hasMore }) => {
  const tableRef = useRef(null);
  const observerRef = useRef(null);
  const loadMoreTriggerRef = useRef(null);

  console.log('summary=', summary);

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          if (onLoadMore) {
            onLoadMore();
          }
        }
      },
      {
        root: tableRef.current,
        rootMargin: "100px",
        threshold: 0.1,
      }
    );

    if (loadMoreTriggerRef.current) {
      observerRef.current.observe(loadMoreTriggerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loadingMore, onLoadMore]);

  const getStatusChip = (status) => {
    const statusConfig = {
      overdue: { bg: "#f8d7da", color: "#721c24", label: "Overdue" },
      urgent: { bg: "#fff3cd", color: "#856404", label: "Urgent" },
      pending: { bg: "#cce5ff", color: "#004085", label: "Pending" },
      paid: { bg: "#d4edda", color: "#155724", label: "Paid" },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Chip
        label={config.label}
        size="small"
        style={{
          backgroundColor: config.bg,
          color: config.color,
          fontSize: "0.7rem",
          fontWeight: 600,
          height: "24px",
        }}
      />
    );
  };

  const getTypeBadge = (type) => {
    const isMiller = type === "miller";
    const displayType = type?.charAt(0)?.toUpperCase() + (type?.slice(1) || "");
    return (
      <span
        style={{
          padding: "4px 10px",
          borderRadius: "12px",
          fontSize: "0.7rem",
          fontWeight: 600,
          backgroundColor: isMiller ? "rgba(241, 154, 48, 0.15)" : "rgba(30, 58, 138, 0.15)",
          color: isMiller ? primary : "#1E3A8A",
        }}
      >
        {displayType || "N/A"}
      </span>
    );
  };

  const DiscountBadge = ({ percentage, isEligible }) => {
    if (!isEligible) return <span style={{ color: "#adb5bd" }}>-</span>;
    return (
      <div className="glowing-gold-badge">
        {percentage}% OFF
      </div>
    );
  };

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      ref={tableRef}
      style={{
        borderRadius: "8px",
        border: "1px solid #e9ecef",
        maxHeight: "calc(90vh - 380px)",
        minHeight: "200px",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow style={{ backgroundColor: "#f8f9fa" }}>
            <TableCell
              style={{
                fontWeight: 600,
                fontSize: "12px",
                color: "#495057",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                padding: "12px",
                borderBottom: "2px solid #e9ecef",
                minWidth: "180px",
              }}
            >
              Entity Name
            </TableCell>
            <TableCell
              align="center"
              style={{
                fontWeight: 600,
                fontSize: "12px",
                color: "#495057",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                padding: "12px",
                borderBottom: "2px solid #e9ecef",
                minWidth: "180px",
              }}
            >
              PO Number
            </TableCell>
            <TableCell
              align="center"
              style={{
                fontWeight: 600,
                fontSize: "12px",
                color: "#495057",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                padding: "12px",
                borderBottom: "2px solid #e9ecef",
                width: "100px",
              }}
            >
              Type
            </TableCell>
            <TableCell
              align="right"
              style={{
                fontWeight: 600,
                fontSize: "12px",
                color: "#495057",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                padding: "12px",
                borderBottom: "2px solid #e9ecef",
                width: "140px",
              }}
            >
              Amount
            </TableCell>
            <TableCell
              align="right"
              style={{
                fontWeight: 600,
                fontSize: "12px",
                color: "#495057",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                padding: "12px",
                borderBottom: "2px solid #e9ecef",
                width: "140px",
              }}
            >
              Paid
            </TableCell>
            <TableCell
              align="right"
              style={{
                fontWeight: 600,
                fontSize: "12px",
                color: "#495057",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                padding: "12px",
                borderBottom: "2px solid #e9ecef",
                width: "140px",
              }}
            >
              Balance
            </TableCell>
            <TableCell
              align="center"
              style={{
                fontWeight: 600,
                fontSize: "12px",
                color: "#495057",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                padding: "12px",
                borderBottom: "2px solid #e9ecef",
                width: "120px",
              }}
            >
              Due Date
            </TableCell>
            <TableCell
              align="center"
              style={{
                fontWeight: 600,
                fontSize: "12px",
                color: "#495057",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                padding: "12px",
                borderBottom: "2px solid #e9ecef",
                width: "120px",
              }}
            >
              Arrival Date
            </TableCell>
            <TableCell
              align="center"
              style={{
                fontWeight: 600,
                fontSize: "12px",
                color: "#495057",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                padding: "12px",
                borderBottom: "2px solid #e9ecef",
                width: "120px",
              }}
            >
              Status
            </TableCell>
            <TableCell
              align="center"
              style={{
                fontWeight: 600,
                fontSize: "12px",
                color: "#495057",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                padding: "12px",
                borderBottom: "2px solid #e9ecef",
                width: "100px",
              }}
            >
              Discount
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!payments || payments.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center" style={{ padding: "40px", color: "#6c757d" }}>
                No due payments found
              </TableCell>
            </TableRow>
          ) : (
            <>
            {console.log("payments", payments)}
              {payments.map((payment, index) => {
                const status = getPaymentStatus(payment?.dueDate);
                const rowClass = status === "overdue" ? "overdue-row" : status === "urgent" ? "urgent-row" : "";
                const isDiscountEligible = payment?.discount?.eligible;
                
                return (
                  <TableRow
                    key={payment?.id || `payment-${index}`}
                    onClick={() => onRowClick && onRowClick(payment)}
                    className={`urgency-row ${rowClass} ${isDiscountEligible ? "discount-eligible-row" : ""}`}
                    style={{
                      cursor: "pointer",
                      backgroundColor: lightGrey,
                    }}
                  >
                    <TableCell style={{ padding: "12px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        {status === "overdue" && <span className="pulsing-red-dot" aria-label="Overdue" />}
                        <span style={{ fontWeight: 500, color: darkBlue }}>
                          {payment?.entityName || payment?.name || "N/A"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell align="center" style={{ padding: "12px" }}>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
                        <span>{payment.poId || ""}</span>
                        <span style={{ fontSize: "0.75rem", color: "#6c757d" }}>Bill No. - <strong>{payment.BillNo || ""}</strong></span>
                      </div>
                    </TableCell>
                    <TableCell align="center" style={{ padding: "12px" }}>
                      {getTypeBadge(payment?.type)}
                    </TableCell>
                    <TableCell align="right" style={{ padding: "12px", fontWeight: 600 }}>
                      {formatCurrency(payment?.amount || payment?.amount || 0, payment?.currency)}
                    </TableCell>
                    <TableCell align="right" style={{ padding: "12px", fontWeight: 600 }}>
                      {formatCurrency(payment?.amountPaid || 0, payment?.currency)}
                    </TableCell>
                    <TableCell align="right" style={{ padding: "12px", fontWeight: 600 }}>
                      {formatCurrency(payment?.amountDue || 0, payment?.currency)}
                    </TableCell>
                    <TableCell align="center" style={{ padding: "12px" }}>
                      {formatDate(payment?.dueDate)}
                    </TableCell>
                    <TableCell align="center" style={{ padding: "12px" }}>
                      {formatDate(payment?.dispatchDate)}
                    </TableCell>
                    <TableCell align="center" style={{ padding: "12px" }}>
                      {getStatusChip(status)}
                    </TableCell>
                    <TableCell align="center" style={{ padding: "12px" }}>
                      <DiscountBadge
                        percentage={payment?.discount?.percentage || 0}
                        isEligible={isDiscountEligible}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
              {hasMore && (
                <TableRow>
                  <TableCell colSpan={6} align="center" style={{ padding: "20px" }}>
                    <div 
                      ref={loadMoreTriggerRef} 
                      style={{ 
                        display: "flex", 
                        justifyContent: "center", 
                        alignItems: "center", 
                        gap: "8px", 
                        color: "#6c757d" 
                      }}
                    >
                      {loadingMore && <CircularProgress size={20} color={primary} />}
                      <span style={{ fontSize: "0.85rem" }}>
                        {loadingMore ? "Loading more..." : "Scroll for more"}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default DuePaymentsTable;
